import { useEffect, useRef } from 'react'

const POLL_INTERVAL_MS = 60000 // 60 seconds

/**
 * Polls `refreshUnreadCount` on a fixed interval.
 *
 * The interval is automatically paused when:
 *  - The Sendbird websocket is actively connected (`isWebsocketActive`),
 *    because real-time events are already supplying the count.
 *
 * The interval restarts (from zero) whenever:
 *  - `resetSignal` changes (e.g. after a navigation-triggered or
 *    websocket-pushed refresh), so we don't poll immediately after
 *    a count was just fetched.
 *  - `isWebsocketActive` transitions from true → false.
 */
export const usePollingUnreadCount = (
  refreshUnreadCount: () => Promise<void>,
  isWebsocketActive: boolean,
  resetSignal: boolean
) => {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    // Clear any existing interval whenever deps change.
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    // Don't poll while the websocket is connected — real-time
    // events are supplying the unread count.
    if (isWebsocketActive) {
      return undefined
    }

    intervalRef.current = setInterval(() => {
      refreshUnreadCount()
    }, POLL_INTERVAL_MS)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [refreshUnreadCount, isWebsocketActive, resetSignal])
}
